import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { NodeData } from '../../../ui/gzTree/type';
import { GzTreeWebComponent } from '../../../ui/gzTree';
import { WEB_COMPONENT_NETWORK_TREE_SEARCH } from '../../../webcomponents.constants';
import { GzNetworkTreeSearchWebComponent } from '../../networkTreeSearch/component/GzNetworkTreeSearchWebComponent';
import { html, TemplateResult } from 'lit';
import { GzNodeDirectorWebComponent } from '../../../ui/gzTree/uiElements/gzNodeDirector';
import { OverlayPositionX, SelectOption } from '../../../ui/gzSelect/types';
import { elementClassListRemove, elementClassListToggle } from '../../../utils/htmlElement.utils';
import { NetworkTreeAction } from '../../networkAppContainer/helpers/types';
import { BaseNetworkActionValidator } from '../../networkAppContainer/factory/action-validators/base.network.action.validator';
import { NetworkActionValidatorsFactory } from '../../networkAppContainer/factory/NetworkActionValidatorsFactory';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { translateFn } from '../../../utils/translator.helper';
import { NetworkTreeEvent, NetworkTreeRenderData, TreeNodeData } from '../types';
import { TreeNodeActionHandlersFactory } from '../actions/tree.node.action.handlers.factory';
import { FrontEndActionEventHandlers } from '../actions/FrontEndActionEventHandlers';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { MegaMenuAction } from '../../networkAppContainer/helpers/megaMenu/types';

export class NetworkTreeView extends BaseWebComponentView {
  private node: TreeNodeData;
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$loader = this.shadow.querySelector('.tree-loader');
    this.dom.$searchTree = this.shadow.querySelector(
      `${WEB_COMPONENT_NETWORK_TREE_SEARCH}`
    ) as GzNetworkTreeSearchWebComponent;

    this.dom.$searchTree.isPartner = Application.Main.company.isPartner();
  }

  buildSuccessView(renderData: NetworkTreeRenderData) {
    if (!renderData.dataSource) {
      return;
    }
    this.dom.$loader.classList.add('hidden');
    this.removeOldTreeIfPresent();

    const newTree = document.createElement('gz-tree') as GzTreeWebComponent<any>;
    newTree.dataSource = renderData.dataSource;
    newTree.translations = {
      loadMoreNodes: translateFn('Load more'),
    };

    if (renderData.hasActions) {
      newTree.nodeRenderer = this.nodeRenderer;
    }

    this.dom.$wrapper.appendChild(newTree);
    this.dom.$tree = newTree;
    this.dom.$tree.part = 'network-content-tree';
    this.dom.$wrapper.dispatchEvent(new CustomEvent(NetworkTreeEvent.TREE_LOADED, { bubbles: true, composed: true }));
  }

  public removeOldTreeIfPresent() {
    const oldTree = this.dom.$wrapper.querySelector('gz-tree');
    if (oldTree) {
      oldTree.remove();
    }
  }

  private nodeRenderer = (
    defaultNodeRenderer: (actionTemplate?: TemplateResult) => TemplateResult,
    nodeElement: GzNodeDirectorWebComponent<NodeData>
  ): TemplateResult => {
    if (
      (nodeElement.state?.rename && nodeElement.node.editState) ||
      nodeElement.nodes.get(nodeElement.nodeId)?.temporaryNode
    ) {
      return this.getRenameTemplate(nodeElement);
    }

    return html`
      ${defaultNodeRenderer(html`
        <span class="node-action" @click=${event => event.stopPropagation()}>
          ${this.getNodeActionsElement(nodeElement)}
        </span>
      `)}
    `;
  };

  private getNodeActionsElement = (node: GzNodeDirectorWebComponent<NodeData>): TemplateResult | null => {
    const selectOptions = this.getSelectOptions(node);

    // Should I actually use a GzListItemWebComponent because it has InlineMenuMixin:ElementHasInlineMenu.renderInlineMenu
    // Which allows for selectDisabled, in order to disable actions when there's already an action going on?
    return html`
      <gz-select
        .overlayPositionX=${OverlayPositionX.RIGHT}
        .options=${selectOptions}
        @overlayClosed="${this.handleFocusOut}"
        @selectionChanged=${event => this.handleTreeActionClicked(event.detail.selected, node)}
      >
        <gz-icon-button
          icon="ds-icon-more"
          fontSize="24"
          buttonType="square"
          buttonFill="quiet"
          buttonColor="secondary"
          @mousedown="${this.handleMouseDown}"
        >
        </gz-icon-button>
      </gz-select>
    `;
  };

  private getSelectOptions(nodeElement: GzNodeDirectorWebComponent<NodeData>) {
    if (!nodeElement.node.nodeId) {
      return [];
    }

    const treeActions: Array<SelectOption> = [
      {
        title: translateFn('Rename'),
        value: NetworkTreeAction.RENAME,
      },
      {
        title: translateFn('Create company folder'),
        value: NetworkTreeAction.CREATE_COMPANY_FOLDER,
      },
      {
        title: translateFn('Create new group'), // network folder
        value: NetworkTreeAction.CREATE_NETWORK_FOLDER,
      },
      {
        title: translateFn('Assign policy'),
        value: MegaMenuAction.ASSIGN_POLICY,
      },
      {
        title: translateFn('Assign Security Servers'),
        value: MegaMenuAction.ASSIGN_SECURITY_SERVERS,
      },
      {
        title: translateFn('Move'),
        value: MegaMenuAction.MOVE_TO,
      },
      {
        title: translateFn('Delete'),
        value: NetworkTreeAction.DELETE,
        customClass: 'danger',
      },
      {
        title: translateFn('Sync with EC2'),
        value: MegaMenuAction.SYNC_WITH_EC2,
      },
    ];

    return this.getValidActions(nodeElement, treeActions);
  }

  private getValidActions(
    node: GzNodeDirectorWebComponent<NodeData>,
    treeActions: Array<SelectOption>
  ): Array<SelectOption> {
    const userData = Application.Main.getUser();
    const record: ProtectedEntity = node.node.originalData;
    const acceptedEc2SyncTypes = [
      Constants.ProtectedEntities.EPS.TYPE_AWS_GROUP,
      Constants.ProtectedEntities.EPS.TYPE_AWS_ACCOUNT,
    ];

    const visibleTreeActions = treeActions.filter(action => {
      switch (action.value) {
        case NetworkTreeAction.CREATE_COMPANY_FOLDER:
          return Application.Main.company.isPartner();
        case MegaMenuAction.ASSIGN_POLICY:
          return Application.Main.hasAllRights([
            Constants.Rights.RIGHT_MANAGE_INVENTORY,
            Constants.Rights.RIGHT_MANAGE_POLICIES_READ,
          ]);
        case MegaMenuAction.ASSIGN_SECURITY_SERVERS:
          return (
            Application.Main.company.isPartner() &&
            Application.Main.hasFeatures([Constants.Features.PRODUCT_FEATURE_REMOTE_ENGINES_SHARING])
          );
        case MegaMenuAction.SYNC_WITH_EC2:
          return (
            (Application.Main.hasAnyRights([Constants.Rights.RIGHT_MANAGE_INVENTORY]) ||
              (Application.Main.company.isCustomer() &&
                Application.Main.hasFeatures([
                  Constants.Features.PRODUCT_FEATURE_AMAZON_EC2_CLOUD_INTEGRATION_CONFIGURED,
                ]))) &&
            acceptedEc2SyncTypes.includes(record.type)
          );
        default:
          return true;
      }
    });

    return visibleTreeActions.map(action => {
      const validator: BaseNetworkActionValidator = NetworkActionValidatorsFactory.create(
        action.value as NetworkTreeAction
      );

      action.disabled = !validator.canPerformAction(record);
      return action;
    });
  }

  private async handleTreeActionClicked(
    treeAction: NetworkTreeAction | MegaMenuAction,
    nodeElement: GzNodeDirectorWebComponent<TreeNodeData>
  ): Promise<void> {
    nodeElement.dataSource.setEditState(nodeElement, true);

    let protectedEntity: ProtectedEntity | null = null;
    try {
      protectedEntity = [MegaMenuAction.ASSIGN_POLICY, NetworkTreeAction.DELETE, MegaMenuAction.MOVE_TO].includes(
        treeAction
      )
        ? await FrontEndActionEventHandlers.getFullProtectedEntityForTreeNode(nodeElement)
        : null;
    } catch (e) {
      return;
    }

    const actionToMethod = {
      [NetworkTreeAction.RENAME]: FrontEndActionEventHandlers.renameNode.bind(FrontEndActionEventHandlers, nodeElement),
      [NetworkTreeAction.CREATE_COMPANY_FOLDER]: FrontEndActionEventHandlers.createCompanyFolder.bind(
        FrontEndActionEventHandlers,
        nodeElement
      ),
      [NetworkTreeAction.CREATE_NETWORK_FOLDER]: FrontEndActionEventHandlers.createNetworkFolder.bind(
        FrontEndActionEventHandlers,
        nodeElement
      ),
      [MegaMenuAction.ASSIGN_POLICY]: FrontEndActionEventHandlers.assignPolicy.bind(
        FrontEndActionEventHandlers,
        protectedEntity,
        this.dom.$wrapper
      ),
      [NetworkTreeAction.DELETE]: FrontEndActionEventHandlers.delete.bind(
        FrontEndActionEventHandlers,
        protectedEntity,
        this.dom.$wrapper
      ),
      [MegaMenuAction.MOVE_TO]: FrontEndActionEventHandlers.moveTo.bind(
        FrontEndActionEventHandlers,
        protectedEntity,
        this.dom.$wrapper
      ),
      [MegaMenuAction.ASSIGN_SECURITY_SERVERS]: FrontEndActionEventHandlers.assignSecurityServers.bind(
        FrontEndActionEventHandlers,
        nodeElement,
        this.dom.$wrapper
      ),
      [MegaMenuAction.SYNC_WITH_EC2]: FrontEndActionEventHandlers.syncWithAwsEc2.bind(
        FrontEndActionEventHandlers,
        nodeElement,
        this.dom.$wrapper
      ),
    };

    const handler = actionToMethod[treeAction];
    if (handler) {
      handler();
    }
  }

  private handleFocusOut(event) {
    const target = event.target as HTMLElement;
    target.removeAttribute('menu-open');

    const parentNode = target.closest('.node') as HTMLElement;
    const selectItem = target.closest('gz-select') as GzSelectWebComponent;
    if (parentNode && !selectItem.hasAttribute('open')) {
      parentNode.classList.remove('node--dropdown-open');
    }

    elementClassListRemove(event.target, 'active');
  }

  private handleMouseDown(event) {
    const target = event.target as HTMLElement;
    target.setAttribute('menu-open', 'true');
    elementClassListToggle(event.target, 'active');

    const parentNode = target.closest('.node') as HTMLElement;
    if (parentNode && !this.node.active) {
      parentNode.classList.add('node--dropdown-open');
    }
  }

  private getRenameTemplate(nodeElement: GzNodeDirectorWebComponent<TreeNodeData>): TemplateResult {
    const stateReason = (nodeElement.node as TreeNodeData).temporaryState;
    const actionHandlers = TreeNodeActionHandlersFactory.create(stateReason);
    if (nodeElement.state?.input === undefined) {
      nodeElement.setState({ ...nodeElement.state, input: nodeElement.node.nodeName });
    }

    return html`
      <gz-node-rename-input
        .maxLength=${512}
        .placeholder=${translateFn('Enter the name')}
        ?isEnabled=${stateReason !== NetworkTreeAction.RENAME}
        .nodeElement=${nodeElement}
        .createNodeRequest=${actionHandlers.createNodeRequest.bind(actionHandlers)}
        .validators=${actionHandlers.validator}
      ></gz-node-rename-input>
    `;
  }
}
